/**
 * Super Admin Promotions Page - 프로모션/쿠폰 관리
 */

import { Trans, t } from '@lingui/macro';
import { Pencil, Trash2, Tag, Calendar, Percent, DollarSign, Loader2, Copy } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { cn } from '../../lib/utils';
import { Fire } from '../../services/FireClient';
import { collection, getDocs, doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { Promotion, PROMOTIONS_COL, defaultPromotion, formatPromotionDiscount, PromotionStatus } from '../../models/Promotion';
import { DiscountType } from '../../models/SubscriptionData';
import { IconPlus } from '../components/FigmaIcons';

const SuperAdminPromotionsPage: React.FC = () => {
    const [promotions, setPromotions] = useState<Promotion[]>([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadPromotions();
    }, []);

    const loadPromotions = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(collection(Fire.store, PROMOTIONS_COL));
            const items = snap.docs.map(doc => {
                const data = doc.data();
                return {
                    ...data,
                    id: doc.id,
                    validFrom: data.validFrom?.toDate() || new Date(),
                    validUntil: data.validUntil?.toDate() || new Date(),
                    createdAt: data.createdAt?.toDate() || new Date(),
                    updatedAt: data.updatedAt?.toDate() || new Date(),
                } as Promotion;
            });
            setPromotions(items.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()));
        } catch (error) {
            console.error('Failed to load promotions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (promo: Promotion) => {
        setEditingPromotion({ ...promo });
        setIsDialogOpen(true);
    };

    const handleCreate = () => {
        const newPromo = defaultPromotion();
        newPromo.id = `promo_${Date.now()}`;
        setEditingPromotion(newPromo);
        setIsDialogOpen(true);
    };

    const handleSave = async () => {
        if (!editingPromotion) return;
        setSaving(true);
        try {
            await setDoc(doc(Fire.store, PROMOTIONS_COL, editingPromotion.id), {
                ...editingPromotion,
                updatedAt: serverTimestamp(),
                createdAt: editingPromotion.createdAt || serverTimestamp(),
            });
            await loadPromotions();
            setIsDialogOpen(false);
            setEditingPromotion(null);
        } catch (error) {
            console.error('Failed to save promotion:', error);
            alert('保存に失敗しました');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (promoId: string) => {
        if (!confirm('このプロモーションを削除しますか？')) return;
        try {
            await deleteDoc(doc(Fire.store, PROMOTIONS_COL, promoId));
            await loadPromotions();
        } catch (error) {
            console.error('Failed to delete promotion:', error);
        }
    };

    const copyCode = (code: string) => {
        navigator.clipboard.writeText(code);
        alert('コードをコピーしました');
    };

    const getStatusColor = (status: PromotionStatus) => {
        switch (status) {
            case 'active': return 'bg-emerald-100 text-emerald-700';
            case 'inactive': return 'bg-slate-100 text-slate-600';
            case 'expired': return 'bg-red-100 text-red-700';
        }
    };

    const isExpired = (promo: Promotion) => {
        return new Date() > promo.validUntil;
    };

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="bg-white/80 backdrop-blur-md border-b border-slate-100/50 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl font-bold text-slate-900"><Trans>Promotions</Trans></h1>
                        <p className="text-sm text-slate-500 mt-0.5">
                            <Trans>Manage discount promotions and coupons</Trans>
                        </p>
                    </div>
                    <Button onClick={handleCreate} className="bg-indigo-600 hover:bg-indigo-700">
                        <IconPlus className="w-4 h-4 mr-2" />
                        <Trans>New Promotion</Trans>
                    </Button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6 bg-slate-50/30">
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                    </div>
                ) : promotions.length === 0 ? (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <Tag className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                            <p className="text-slate-500"><Trans>No promotions yet</Trans></p>
                            <Button onClick={handleCreate} className="mt-4">
                                <IconPlus className="w-4 h-4 mr-2" />
                                <Trans>Create First Promotion</Trans>
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid gap-4">
                        {promotions.map(promo => (
                            <Card key={promo.id} className={cn(
                                'transition-all hover:shadow-md',
                                isExpired(promo) && 'opacity-60'
                            )}>
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="font-semibold text-slate-900">{promo.name}</h3>
                                                <Badge className={getStatusColor(isExpired(promo) ? 'expired' : promo.status)}>
                                                    {isExpired(promo) ? 'Expired' : promo.status}
                                                </Badge>
                                            </div>

                                            <div className="flex items-center gap-4 text-sm text-slate-600 mb-2">
                                                <span className="flex items-center gap-1">
                                                    {promo.discountType === 'percent'
                                                        ? <Percent className="w-3.5 h-3.5" />
                                                        : <DollarSign className="w-3.5 h-3.5" />
                                                    }
                                                    <span className="font-medium text-indigo-600">
                                                        {formatPromotionDiscount(promo)}
                                                    </span>
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Calendar className="w-3.5 h-3.5" />
                                                    {promo.validFrom.toLocaleDateString('ja-JP')} ~ {promo.validUntil.toLocaleDateString('ja-JP')}
                                                </span>
                                                {promo.maxUses && promo.maxUses > 0 && (
                                                    <span>
                                                        使用: {promo.usedCount}/{promo.maxUses}
                                                    </span>
                                                )}
                                            </div>

                                            {promo.code && (
                                                <div className="flex items-center gap-2">
                                                    <code className="px-2 py-0.5 bg-slate-100 rounded text-sm font-mono">
                                                        {promo.code}
                                                    </code>
                                                    <button
                                                        onClick={() => copyCode(promo.code!)}
                                                        className="text-slate-400 hover:text-slate-600"
                                                    >
                                                        <Copy className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            )}

                                            {promo.description && (
                                                <p className="text-sm text-slate-500 mt-2">{promo.description}</p>
                                            )}
                                        </div>

                                        <div className="flex gap-2">
                                            <Button variant="ghost" size="icon" onClick={() => handleEdit(promo)}>
                                                <Pencil className="w-4 h-4" />
                                            </Button>
                                            <Button variant="ghost" size="icon" onClick={() => handleDelete(promo.id)}>
                                                <Trash2 className="w-4 h-4 text-red-500" />
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>

            {/* Edit Dialog */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            {editingPromotion?.createdAt ? <Trans>Edit Promotion</Trans> : <Trans>New Promotion</Trans>}
                        </DialogTitle>
                    </DialogHeader>

                    {editingPromotion && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label><Trans>Name</Trans> *</Label>
                                    <Input
                                        placeholder={t`20% OFF first contract`}
                                        value={editingPromotion.name}
                                        onChange={(e) => setEditingPromotion({ ...editingPromotion, name: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label><Trans>Code</Trans> (任意)</Label>
                                    <Input
                                        placeholder="WELCOME2026"
                                        value={editingPromotion.code || ''}
                                        onChange={(e) => setEditingPromotion({ ...editingPromotion, code: e.target.value.toUpperCase() })}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label><Trans>Discount Type</Trans></Label>
                                    <Select
                                        value={editingPromotion.discountType}
                                        onValueChange={(v) => setEditingPromotion({ ...editingPromotion, discountType: v as DiscountType })}
                                    >
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="percent">% (Percent)</SelectItem>
                                            <SelectItem value="fixed">¥ (Fixed Amount)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>
                                        {editingPromotion.discountType === 'percent' ? 'Discount %' : 'Discount ¥'}
                                    </Label>
                                    <Input
                                        type="number"
                                        placeholder={editingPromotion.discountType === 'percent' ? '20' : '2000'}
                                        value={editingPromotion.discountValue || ''}
                                        onChange={(e) => setEditingPromotion({ ...editingPromotion, discountValue: Number(e.target.value) })}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label><Trans>Valid From</Trans></Label>
                                    <Input
                                        type="date"
                                        value={editingPromotion.validFrom.toISOString().split('T')[0]}
                                        onChange={(e) => setEditingPromotion({ ...editingPromotion, validFrom: new Date(e.target.value) })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label><Trans>Valid Until</Trans></Label>
                                    <Input
                                        type="date"
                                        value={editingPromotion.validUntil.toISOString().split('T')[0]}
                                        onChange={(e) => setEditingPromotion({ ...editingPromotion, validUntil: new Date(e.target.value) })}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label><Trans>Max Uses</Trans> (0=無制限)</Label>
                                    <Input
                                        type="number"
                                        placeholder="0"
                                        value={editingPromotion.maxUses || ''}
                                        onChange={(e) => setEditingPromotion({ ...editingPromotion, maxUses: Number(e.target.value) })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label><Trans>Status</Trans></Label>
                                    <Select
                                        value={editingPromotion.status}
                                        onValueChange={(v) => setEditingPromotion({ ...editingPromotion, status: v as PromotionStatus })}
                                    >
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="active">Active</SelectItem>
                                            <SelectItem value="inactive">Inactive</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label><Trans>Description</Trans></Label>
                                <Textarea
                                    placeholder={t`Promotion description...`}
                                    value={editingPromotion.description || ''}
                                    onChange={(e) => setEditingPromotion({ ...editingPromotion, description: e.target.value })}
                                />
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                            <Trans>Cancel</Trans>
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={saving || !editingPromotion?.name}
                            className="bg-indigo-600 hover:bg-indigo-700"
                        >
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            <Trans>Save</Trans>
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SuperAdminPromotionsPage;
